import { Amplify, ResourcesConfig } from 'aws-amplify';
import { sessionStorage } from 'ALIASED-aws-amplify/utils';
import { cognitoUserPoolsTokenProvider } from 'ALIASED-aws-amplify/auth/cognito';
import { Config } from '../hooks/Runtime';

type AmplifyConfig = Parameters<typeof Amplify.configure>[0];
type CognitoConfig = NonNullable<NonNullable<ResourcesConfig['Auth']>['Cognito']>;
type OauthConfig = NonNullable<CognitoConfig['loginWith']>['oauth'];

function choosePoolClientId(APP_CLIENT_ID: string, APP_CLIENT_ID2: string) {
  for (const key of Object.keys(window.sessionStorage)) {
    if (key.startsWith('CognitoIdentityServiceProvider.') && key.endsWith('.LastAuthUser')) {
      const clientId = key.split('.')[1];

      if (clientId !== APP_CLIENT_ID && clientId !== APP_CLIENT_ID2) throw Error('Unexpected LastAuthUser');

      return clientId;
    }
  }

  // the user isn't signed in ... use the window location ... might be on a story branch
  if (window.location.pathname.match(/\/(axa|link)login/)) {
    return APP_CLIENT_ID2;
  }

  return APP_CLIENT_ID;
}

function getOAuthConfig(
  identityProviders: Config['IDENTITY_PROVIDERS'],
  LUCY_ENV?: string,
  API_URL?: string
): OauthConfig {
  const { domain, redirectSignOut, redirectSignIn } = identityProviders || {};

  if (!domain || !redirectSignIn || !redirectSignOut) return undefined;

  const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/)
  );

  function getRedirectSignOutLink() {
    if (!identityProviders?.redirectSignOut) {
      throw Error('IDENTITY_PROVIDERS.redirectSignOut must be provided');
    }
    return `${isLocalhost ? window.location.origin : identityProviders.redirectSignOut}`;
  }

  function SSORedirectSignOut() {
    if (LUCY_ENV?.includes('npre')) {
      return getRedirectSignOutLink();
    }
    if (LUCY_ENV?.includes('pre')) {
      return `${API_URL}/axapatlogout`;
    }
    if (LUCY_ENV?.includes('nprod')) {
      return getRedirectSignOutLink();
    }
    if (LUCY_ENV?.includes('prod')) {
      return `${API_URL}/axapatlogout`;
    }

    return getRedirectSignOutLink();
  }

  return {
    domain: domain,
    scopes: ['email', 'openid', 'aws.cognito.signin.user.admin', 'profile'],
    redirectSignIn: [`${isLocalhost ? window.location.origin : redirectSignIn}`],
    redirectSignOut: [SSORedirectSignOut()],
    responseType: 'code'
  };
}

const setAmplifyForLocalHost = ({
  IDENTITY_PROVIDERS,
  APP_USER_POOL_ID,
  APP_CLIENT_ID,
  APP_CLIENT_ID2,
  LUCY_ENV,
  API_URL
}: Config) => {
  const config: AmplifyConfig = {
    Auth: {
      Cognito: {
        userPoolId: APP_USER_POOL_ID,
        userPoolClientId: choosePoolClientId(APP_CLIENT_ID, APP_CLIENT_ID2),
        loginWith: { oauth: getOAuthConfig(IDENTITY_PROVIDERS, LUCY_ENV, API_URL) }
      }
    }
  };

  cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
  Amplify.configure(config);
};

export { setAmplifyForLocalHost };
